import { Box, Typography } from "@mui/material";
import { ReactElement } from "react";

import {
  CampaignSortBy,
  CampaignStatus,
} from "~frontend/__generated__/graphql";
import { LinkButton } from "~frontend/components/Link";
import { TableControls } from "~frontend/components/Table";
import TooltipButton from "~frontend/components/TooltipButton";
import {
  CAMPAIGN_SORTS,
  CAMPAIGN_STATUSES,
} from "~frontend/routes/subaccounts/campaignsList/constants";
import { CampaignWithSuppressionData } from "~frontend/routes/types";
import theme, { palette } from "~frontend/theme";

export default function OverlapListControls({
  campaign,
  onClickRecalculate,
  statusFilter,
  setStatusFilter,
  search,
  setSearch,
  sort,
  setSort,
  createdAtWindowDays,
  lastRecalculatedAt,
  setCreatedAtWindowDays,
  disableRecalculate,
  onClickSuppressAll,
  disableSuppressAll,
}: {
  campaign: CampaignWithSuppressionData;
  onClickRecalculate: () => Promise<void>;
  statusFilter: string[];
  setStatusFilter: (newStatusFilter: CampaignStatus[]) => void;
  search: string;
  setSearch: (newSearch: string) => void;
  sort: string;
  setSort: (newSort: CampaignSortBy) => void;
  createdAtWindowDays: number;
  lastRecalculatedAt: Date | null;
  setCreatedAtWindowDays: (newCreatedAtWindowDays: number) => void;
  disableRecalculate: boolean;
  onClickSuppressAll: () => void;
  disableSuppressAll: boolean;
}): ReactElement {
  const sendInProgress =
    campaign.status === CampaignStatus.InitialSendRunning ||
    campaign.status === CampaignStatus.InitialSendCanceling;

  return (
    <TableControls
      paperStyles={{
        backgroundColor: palette.lightGray,
        padding: theme.spacing(2),
      }}
      filters={[
        {
          id: "status",
          label: "Campaign Status",
          value: statusFilter,
          onChange: setStatusFilter as (newStatusFilter: string[]) => void,
          options: CAMPAIGN_STATUSES.map(status => ({
            value: status.value,
            label: status.label,
          })),
          multi: true,
        },
        {
          id: "createdAtWindowDays",
          type: "number",
          label: "Created in the last",
          unit: "days",
          value: createdAtWindowDays,
          onChange: setCreatedAtWindowDays,
        },
      ]}
      sort={{
        value: sort,
        onChange: setSort as (newSort: string) => void,
        options: CAMPAIGN_SORTS.map(sort => ({
          value: sort.value,
          label: sort.label,
        })),
      }}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: "Search for a campaign...",
        sx: { border: `1px solid ${palette.midGray} !important` },
      }}
      controlsFooter={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "14px",
            marginTop: "-14px",
            paddingTop: theme.spacing(2),
          }}
        >
          <Box>
            {lastRecalculatedAt && (
              <Typography variant="body3">
                Last Calculated {lastRecalculatedAt.toLocaleString()}.
              </Typography>
            )}
            {!disableRecalculate && (
              <LinkButton
                onClick={onClickRecalculate}
                sx={{ marginLeft: 2, fontSize: "10px", fontWeight: 700 }}
              >
                Refresh Campaigns
              </LinkButton>
            )}
          </Box>
          <TooltipButton
            variant="contained"
            color="primary"
            onClick={onClickSuppressAll}
            disabled={disableSuppressAll || sendInProgress}
            tooltipText={
              sendInProgress
                ? "Cannot suppress contacts while sending is in progress"
                : undefined
            }
            sx={{ fontSize: "10px", padding: "6px 8px", height: "auto" }}
          >
            Suppress All Overlaps
          </TooltipButton>
        </Box>
      }
    />
  );
}
